import type { ApprovalEditForm } from '~/features/chat/approval-edit-draft';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { approvalKeys } from '~/lib/query-keys';

export interface Approval {
  id: string;
  type: string;
  description: string;
  details?: any;
  /** 'pending' for anything the list returns today; kept explicit so a
   *  consumer can defend against a resolved row arriving mid-refetch. */
  status?: 'pending' | 'approved' | 'rejected';
  /**
   * FRONT-1113: the chat surface this approval was raised from (that chat's
   * `web:`/`inbox-` session id), or null when it has no chat origin. The chat
   * matches it against its own `sessionId` to render the approval inline in
   * the conversation that's blocked on it.
   */
  originExternalId?: string | null;
  /** When the user was first asked — ISO timestamp. Used to show "asked N
   *  min ago" on a `deferred` row, since that request may be up to two hours
   *  old (FRONT-2307's late-approval window). */
  requestedAt?: string;
  /**
   * FRONT-1639: `"deferred"` once the autonomy gate gave up polling and left
   * the row open instead of auto-rejecting it (closing the chat used to
   * destroy the request outright) — for up to two hours, FRONT-2307. `status` stays `"pending"` either
   * way; absent/`"live"` is a fresh, still-being-polled row.
   */
  waitingMode?: 'live' | 'deferred';
  /** FRONT-2307: when this approval stops being answerable — the server's
   *  late-approval window, sent so the card's "approve before" line can't
   *  drift from what the server enforces. */
  answerableUntil?: string;
  /**
   * FRONT-1747: the form "Edit" opens, seeded by the server from the
   * proposal — only on the approval kinds that can be edited (a memory save,
   * a document rewrite). Absent or null keeps the card Yes/No only.
   */
  editForm?: ApprovalEditForm | null;
}

export function useApprovals() {
  return useQuery({
    queryKey: approvalKeys.list(),
    queryFn: async () => {
      const data = await api.get<any>('/api/approvals');
      return (Array.isArray(data) ? data : data.approvals ?? []) as Approval[];
    },
    // SSE invalidates this on `approval-request` / `approval-decided`
    // (see use-sse.ts). FRONT-2343: `useChatStatuses`'s poll also invalidates
    // this list (`approvalsCacheDisagrees`, ./approvals-reconcile.ts) when it
    // disagrees with Postgres — the cross-replica-safe path, driven by an
    // active chat or a live hands-free call (FRONT-3118). This 5-minute
    // poll is the last-resort fallback for everything else (the SSE socket
    // dropped silently, or no chat is active to drive the faster path) —
    // cheap and rare.
    refetchInterval: 5 * 60_000,
  });
}
